import React, {
  FC,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useRef,
} from "react"
import { Table } from "@/components/general/Table"
import { AssignedUsersType } from "@/lib/types"
import { LocalStorage } from "@/lib/types/LocalStorage"
import { Localstorage } from "@/lib/services"
import { Modal } from "@/components/general/Modal"
import { Role, Search } from "@/lib/types"
import { useMutation } from "react-query"
import { api } from "@/lib/api"
import { UserPlusIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline"
import Swal from "sweetalert2"
type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  unAssignedUsersListingData: { RateEngine: AssignedUsersType[] }
  assignedUsersListingData: { RateEngine: AssignedUsersType[] }
  recentCustomersListingData: { users: AssignedUsersType[] }
  header: {
    name: string
    width: string
  }[]
  handleAssign: (form: AssignedUsersType) => void
  handleAssignCustomers: (form: AssignedUsersType) => void
  isAssigning: boolean
  setId: Dispatch<SetStateAction<string>>
  role: Role
  salesRepId: string
  slug: string
  handleFormDelete: (form: { userID: string; approved: boolean }) => void
}

type SalesRepSearchResult = {
  id: string
  firstName: string
  lastName: string
  email: string
}

export const AssignUsers: FC<Props> = (props) => {
  const searchInput = useRef<HTMLInputElement>(null)
  const user = Localstorage.get<LocalStorage>("next.user")
  const [assign, setAssign] = useState({
    salesRepID: "",
    userId: "",
  })
  const [selectedCustomerName, setSelectedCustomerName] = useState("")
  const [searchInputValue, setSearchInputValue] = useState("")
  const [activeResultIndex, setActiveResultIndex] = useState(0)

  const mutation = useMutation<
    { topMatchingUsers: SalesRepSearchResult[] },
    Error,
    Search
  >((payload) => api.AdminApi.Search(payload))
  const {
    mutate: searchSalesReps,
    data: salesRepSearchResults,
    isLoading: isSearchingSalesReps,
  } = mutation

  useEffect(() => {
    if (!props.popup) return
    setSearchInputValue("")
    setActiveResultIndex(0)
    searchSalesReps({
      input: "",
      userRole: "SALESREP",
      salesRepID: "",
    })
    requestAnimationFrame(() => searchInput.current?.focus())
  }, [props.popup, searchSalesReps])

  const onAssign = (e: AssignedUsersType) => {
    setAssign(() => ({
      userId: e.id,
      salesRepID: e.salesRepID,
    }))
    setSelectedCustomerName(`${e.firstName} ${e.lastName}`)
    props.setPopup(true)
  }

  const updateSearch = (input: string) => {
    setSearchInputValue(input)
    setActiveResultIndex(0)
    searchSalesReps({
      input,
      userRole: "SALESREP",
      salesRepID: "",
    })
  }

  const assignToSalesRep = (salesRepID: string) => {
    if (!assign.userId || props.isAssigning) return
    props.handleAssignCustomers({
      userId: assign.userId,
      salesRepID,
    } as AssignedUsersType)
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to reject this User?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: "custom-popup",
      },
      confirmButtonText: "<p style='color: black;'>Yes, Reject this!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete({ userID: id, approved: false })
        Swal.fire({
          icon: "success",
          title: "User Rejected",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          },
        })
      }
    })
  }
  return (
    <>
      <Modal
        open={props.popup}
        close={() => !props.isAssigning && props.setPopup(false)}
        width="small"
        title={selectedCustomerName ? `Assign ${selectedCustomerName}` : "Assign customer"}
      >
        <div className="flex flex-col gap-3 pt-2">
          <label className="text-sm dark:text-primary-2 text-light-2" htmlFor="sales-rep-search">
            Search sales reps
          </label>
          <div className="flex items-center rounded border-2 dark:border-secondary-2">
            <input
              ref={searchInput}
              autoFocus
              id="sales-rep-search"
              className="w-full px-3 py-2 text-sm dark:bg-primary-1 bg-white outline-none dark:text-white text-light-2"
              placeholder="Start typing a name or email"
              value={searchInputValue}
              disabled={props.isAssigning}
              onChange={(event) => updateSearch(event.target.value)}
              onKeyDown={(event) => {
                const results = salesRepSearchResults?.topMatchingUsers ?? []
                if (event.key === "ArrowDown") {
                  event.preventDefault()
                  setActiveResultIndex((index) => Math.min(index + 1, results.length - 1))
                } else if (event.key === "ArrowUp") {
                  event.preventDefault()
                  setActiveResultIndex((index) => Math.max(index - 1, 0))
                } else if (event.key === "Enter" && results[activeResultIndex]) {
                  event.preventDefault()
                  assignToSalesRep(results[activeResultIndex].id)
                }
              }}
            />
            <MagnifyingGlassIcon className="w-5 h-5 mr-3 dark:text-primary-2 text-light-2" />
          </div>
          <div className="max-h-56 overflow-auto rounded border-2 dark:border-secondary-2 divide-y dark:divide-primary-4/40">
            {isSearchingSalesReps ? (
              <p className="px-3 py-4 text-sm dark:text-primary-2 text-light-2">Loading sales reps…</p>
            ) : salesRepSearchResults?.topMatchingUsers?.length ? (
              salesRepSearchResults.topMatchingUsers.map((salesRep, index) => (
                <button
                  className={`w-full px-3 py-3 text-left text-sm dark:text-primary-2 text-light-2 hover:bg-white/5 ${
                    index === activeResultIndex ? "bg-white/5" : ""
                  }`}
                  disabled={props.isAssigning}
                  key={salesRep.id}
                  onClick={() => assignToSalesRep(salesRep.id)}
                  type="button"
                >
                  <span className="block font-medium">{salesRep.firstName} {salesRep.lastName}</span>
                  <span className="block text-xs opacity-75">{salesRep.email}</span>
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-sm dark:text-primary-2 text-light-2">No sales reps found.</p>
            )}
          </div>
          <p className="text-xs dark:text-primary-2 text-light-2">
            Select a sales rep to assign this customer immediately.
          </p>
        </div>
      </Modal>
      <div className="md:m-4 m-2">
        <Table
          header={props.header.slice(0, props.header.length - 1)}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.unAssignedUsersListingData.RateEngine.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-56"}>
                {e.firstName} {e.lastName}
              </td>
              <td className={"whitespace-nowrap p-4 w-80"}>{e.email}</td>
              <td className={"whitespace-nowrap p-4 w-64"}>{e.companyName}</td>
              <td className={"whitespace-nowrap p-4 w-64"}>
                <div className="flex justify-end gap-x-2">
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                  {user?.role == "ADMIN" ? (
                    <UserPlusIcon
                      className="w-5 h-5 text-accent cursor-pointer"
                      onClick={() => onAssign(e)}
                    />
                  ) : (
                    <UserPlusIcon
                      className="w-5 h-5 text-accent cursor-pointer"
                      onClick={() => props.handleAssign(e)}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}

export default AssignUsers
